/**
 * Окно карточки Яндекс Маркета на каркасе сущности.
 *
 * Общий каркас (`McardWindow`) даёт шапку, цепочку, все проверки, единые
 * вкладки и правку текста и характеристик через журнал правок. У Маркета своё:
 * разбор замечаний, характеристики со справочником категории, фото со статусами
 * загрузки на Маркет. Фото в окне только читаются; заменить их можно кнопкой
 * «Обновить фото из ПИМ…».
 *
 * «Перечитать данные» берёт сохранённую копию карточки из ПИМ и площадку не
 * опрашивает — в подсказке кнопки это сказано прямо.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Copy } from 'lucide-react'
import { Badge } from '../../../components/ui/Badge'
import { useToastStore } from '../../../stores/toastStore'
import { McardWindow } from '../../marketplace-catalog/components/mp-card/McardWindow'
import { Notice, Panel, ReadonlyValue } from '../../marketplace-catalog/components/mp-card/McardParts'
import {
  formatRub,
  type McardCharacteristic,
  type McardIssue,
  type McardModel,
  type McardShowcase,
} from '../../marketplace-catalog/components/mp-card/model'
import { yandexMarketAPI, type YmCategoryParameter } from '../api'
import {
  YM_ERROR_STATUSES,
  YM_MODERATION_STATUSES,
  YM_NEED_CONTENT_STATUSES,
  YM_SHOWCASE_STATUSES,
  rowKeyOf,
  ymStatusLabel,
  ymStatusVariant,
} from './columns'
import { YmPhotoThumb, ymPhotoStatusLabel, ymPhotoStatusVariant, ymPhotoUnavailableHint } from './photoStatus'
import type { YmCatalogProduct, YmIssueRaw, YmParameterValueRaw } from './types'

interface YmProductModalProps {
  sellerId: string | null
  sellerName?: string | null
  offerId: string | null
  keys: string[]
  rows: YmCatalogProduct[]
  onNavigate: (key: string) => void
  onClose: () => void
  /** Окно открыто навигатором по цепочке: есть куда вернуться (кнопка и Alt+←). */
  canGoBack?: boolean
  onBack?: () => void
}

/**
 * Замечание площадки (`errors`/`warnings`) в пригодном для чтения виде.
 *
 * НЕ ПРОВЕРЕНО на живых данных — Партнёрский API Я.Маркета ещё ни разу не
 * синхронизировался с этим кабинетом (Этап 1, только чтение). Читаем защитно
 * по нескольким вероятным именам поля; если формат окажется другим, в
 * крайнем случае покажется JSON целиком, а не пустая строка.
 */
function normalizeYmIssue(raw: YmIssueRaw): { title: string; text: string } {
  const title = String(raw?.code || raw?.type || 'Замечание').trim()
  const text = String(raw?.message || raw?.description || '').trim()
  if (text) return { title, text }
  try {
    return { title, text: JSON.stringify(raw) }
  } catch {
    return { title, text: 'Площадка не пояснила замечание' }
  }
}

/** id характеристики из значения — Партнёрский API может назвать поле по-разному. */
function parameterIdOf(value: YmParameterValueRaw): number | null {
  const id = value?.parameterId ?? value?.parameter_id ?? value?.paramId
  return id === undefined || id === null ? null : Number(id)
}

function parameterValueText(value: YmParameterValueRaw, definition?: YmCategoryParameter): string {
  if (value?.value !== undefined && value?.value !== null && value.value !== '') {
    return `${value.value}${value.unit ? ` ${value.unit}` : ''}`
  }
  const valueId = value?.valueId ?? value?.value_id
  if (valueId !== undefined && valueId !== null) {
    const known = definition?.values?.find((item) => Number(item.value_id) === Number(valueId))
    return known?.value ?? String(valueId)
  }
  return ''
}

/** Характеристики окна: значения карточки + обязательные параметры категории без значения. */
export const ymCharacteristics = (
  product: YmCatalogProduct | null,
  definitions: Record<number, YmCategoryParameter>,
): McardCharacteristic[] => {
  if (!product) return []
  const values: YmParameterValueRaw[] = Array.isArray(product.parameter_values)
    ? (product.parameter_values as YmParameterValueRaw[])
    : []
  const byId = new Map<number, string[]>()
  const order: Array<number | string> = []
  values.forEach((value, index) => {
    const id = parameterIdOf(value)
    const key = id !== null ? id : `no-id-${index}`
    const text = parameterValueText(value, id !== null ? definitions[id] : undefined)
    if (typeof key === 'number') {
      if (!byId.has(key)) {
        byId.set(key, [])
        order.push(key)
      }
      if (text) byId.get(key)!.push(text)
    } else {
      order.push(key)
    }
  })
  const rows: McardCharacteristic[] = []
  for (const key of order) {
    if (typeof key === 'number') {
      const definition = definitions[key]
      rows.push({
        key: String(key),
        targetId: key,
        name: definition?.name || `ID ${key}`,
        required: Boolean(definition?.required),
        value: (byId.get(key) ?? []).join(', '),
        hint: `Маркет ID ${key}${definition?.unit ? ` · ${definition.unit}` : ''}`,
      })
    } else {
      rows.push({ key, targetId: null, name: 'Характеристика без id', required: false, value: '' })
    }
  }
  for (const definition of Object.values(definitions)) {
    if (!definition.required || byId.has(Number(definition.parameter_id))) continue
    rows.push({
      key: String(definition.parameter_id),
      targetId: Number(definition.parameter_id),
      name: definition.name,
      required: true,
      value: '',
      hint: `Маркет ID ${definition.parameter_id}`,
    })
  }
  return rows
}

const showcaseOf = (product: YmCatalogProduct): McardShowcase => {
  if (product.archived) {
    return {
      tone: 'bad',
      value: 'в архиве',
      problem: {
        title: 'Карточка в архиве кабинета — на витрине её нет',
        hint: 'Зеркало ПИМ только читает каталог: вернуть карточку из архива можно в кабинете Маркета.',
      },
    }
  }
  const status = product.card_status as string
  if ((YM_SHOWCASE_STATUSES as string[]).includes(status)) return { tone: 'ok', value: 'на витрине' }
  if (status === 'HAS_CARD_CAN_UPDATE_ERRORS') {
    return {
      tone: 'warn', value: 'на витрине, правка отклонена',
      problem: {
        title: 'Маркет отклонил правку существующей карточки',
        hint: 'Карточка остаётся на витрине с прежним контентом. Причина отклонения — во вкладке «Обзор».',
      },
    }
  }
  if (status === 'HAS_CARD_CAN_UPDATE_PROCESSING') {
    return {
      tone: 'warn', value: 'на витрине, правка на модерации',
      problem: {
        title: 'Правка существующей карточки на модерации Маркета',
        hint: 'Карточка остаётся на витрине с прежним контентом, пока Маркет проверяет правку.',
      },
    }
  }
  if ((YM_ERROR_STATUSES as string[]).includes(status) || product.has_errors) {
    return {
      tone: 'bad',
      value: 'с ошибками',
      problem: { title: 'Маркет отметил карточку ошибкой', hint: 'Подробности — во вкладке «Обзор».' },
    }
  }
  if ((YM_MODERATION_STATUSES as string[]).includes(status)) {
    return {
      tone: 'warn',
      value: 'на модерации',
      problem: { title: 'Карточка на модерации Маркета', hint: 'Пока модерация не закончилась, на витрине её нет или она старая.' },
    }
  }
  if ((YM_NEED_CONTENT_STATUSES as string[]).includes(status)) {
    return {
      tone: 'warn',
      value: 'нужен контент',
      problem: { title: 'Маркету нужен контент для карточки', hint: 'Заполните характеристики и фото, чтобы карточка появилась на витрине.' },
    }
  }
  return { tone: 'ok', value: ymStatusLabel(status) || 'на витрине' }
}

export const buildYmModel = (
  product: YmCatalogProduct,
  offerId: string,
  definitions: Record<number, YmCategoryParameter>,
  sellerName?: string | null,
  sellerId?: string | null,
): McardModel => {
  const pictures =
    Array.isArray(product.pictures) && product.pictures.length > 0
      ? product.pictures
      : product.main_picture
        ? [product.main_picture]
        : []
  const photoStates = pictures.map(
    (_, index) => product.picture_states?.[index]?.upload_state ?? (index === 0 ? product.main_picture_state ?? null : null),
  )
  const errors: YmIssueRaw[] = Array.isArray(product.errors) ? (product.errors as YmIssueRaw[]) : []
  const warnings: YmIssueRaw[] = Array.isArray(product.warnings) ? (product.warnings as YmIssueRaw[]) : []
  const issues: McardIssue[] = [
    ...errors.map((raw) => ({ level: 'error' as const, ...normalizeYmIssue(raw) })),
    ...warnings.map((raw) => ({ level: 'warning' as const, ...normalizeYmIssue(raw) })),
  ]
  if (errors.length === 0 && product.has_errors) {
    issues.push({
      level: 'error',
      title: 'Ошибка карточки',
      text: 'Маркет отметил карточку ошибкой, подробности площадка не прислала.',
    })
  }
  const dimensions = product.weight_dimensions
  const barcodes = Array.isArray(product.barcodes) ? product.barcodes : []
  return {
    platform: 'ym',
    article: product.offer_id || offerId,
    title: String(product.name || ''),
    description: String(product.description || ''),
    brand: product.vendor ? String(product.vendor) : null,
    statusLabel: ymStatusLabel(product.card_status),
    statusVariant: ymStatusVariant(product.card_status),
    showcase: {
      ...showcaseOf(product),
      onShowcase: !product.archived && String(product.card_status).startsWith('HAS_CARD_'),
    },
    photos: pictures,
    photoStates,
    price: {
      shown: product.basic_price != null ? Number(product.basic_price) : null,
      currency: product.currency,
    },
    stock: null,
    stockWhy: 'Партнёрский API Маркета остатков в зеркале не даёт',
    issues,
    characteristics: ymCharacteristics(product, definitions),
    specsKnown: Object.keys(definitions).length > 0,
    lastSyncAt: product.last_sync_at ?? null,
    platformUrl: null,
    idMeta: [
      { label: 'Market SKU', value: product.market_sku ? String(product.market_sku) : null, mono: true, copy: true },
      { label: 'Группа', value: product.group_id ? String(product.group_id) : null, mono: true, copy: true },
    ],
    barcode: barcodes.length ? barcodes.join(', ') : null,
    cabinetLabel: sellerName || (sellerId ? `#${sellerId}` : ''),
    designCode: product.design_code ? String(product.design_code) : null,
    pimArticle: product.pim_article ?? null,
    themeNumber: product.theme_number ?? null,
    dimensionsText: dimensions
      ? `${dimensions.length ?? '—'}×${dimensions.width ?? '—'}×${dimensions.height ?? '—'} мм · ${dimensions.weight ?? '—'} г`
      : null,
    categoryName: product.market_category_name ?? null,
    bundleExternalId: product.group_id ?? null,
  }
}

/**
 * Строка одного фото карточки: превью (та же нейтральная заглушка при
 * ошибке), ссылка продавца моноширинным с усечением и копированием, бейдж
 * статуса загрузки Маркета, пометка «источник недоступен», если картинка не
 * загрузилась в браузере (решение владельца, SPEC §4.2).
 */
const PhotoRow: React.FC<{
  url: string
  index: number
  uploadState: string | null
  onOpen: () => void
}> = ({ url, index, uploadState, onOpen }) => {
  const { showToast } = useToastStore()
  const [loadFailed, setLoadFailed] = useState(false)

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url)
      showToast({ type: 'success', title: 'Ссылка скопирована', message: url, duration: 3000 })
    } catch {
      showToast({
        type: 'error',
        title: 'Не удалось скопировать',
        message: 'Браузер отклонил доступ к буферу обмена',
        duration: 5000,
      })
    }
  }

  return (
    <div className="flex items-center gap-3 rounded-md border border-gray-200 p-2.5 dark:border-zinc-800">
      <button
        type="button"
        onClick={onOpen}
        disabled={loadFailed}
        className="shrink-0 disabled:cursor-default"
        title={loadFailed ? undefined : 'Открыть в полном размере'}
      >
        <YmPhotoThumb
          src={url}
          uploadState={uploadState}
          alt={`Фото ${index + 1}`}
          size="h-16 w-16"
          onLoadError={() => setLoadFailed(true)}
        />
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-gray-700 dark:text-zinc-300" title={url}>
            {url}
          </span>
          <button
            type="button"
            title="Скопировать ссылку"
            onClick={() => void copyUrl()}
            className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Badge variant={ymPhotoStatusVariant(uploadState)}>{ymPhotoStatusLabel(uploadState)}</Badge>
          {loadFailed && (
            <span className="text-xs text-gray-500 dark:text-zinc-400">{ymPhotoUnavailableHint(uploadState)}</span>
          )}
        </div>
      </div>
    </div>
  )
}

export const YmProductModal: React.FC<YmProductModalProps> = ({
  sellerId,
  sellerName,
  offerId,
  keys,
  rows,
  onNavigate,
  onClose,
  canGoBack,
  onBack,
}) => {
  const { showToast } = useToastStore()
  const [detail, setDetail] = useState<YmCatalogProduct | null>(null)
  const [loading, setLoading] = useState(false)
  /** Первая загрузка полной карточки закончилась (успехом или ошибкой): до этого окно показывает скелетоны. */
  const [detailSettled, setDetailSettled] = useState(false)
  const [parameterNames, setParameterNames] = useState<Record<number, YmCategoryParameter>>({})

  const listRow = useMemo(
    () => rows.find((row) => (row.offer_id || String(row.id)) === offerId) || null,
    [rows, offerId],
  )
  const product = useMemo<YmCatalogProduct | null>(
    () => (listRow || detail ? ({ ...(listRow || {}), ...(detail || {}) } as YmCatalogProduct) : null),
    [listRow, detail],
  )

  const loadDetail = useCallback(async () => {
    if (!sellerId || !offerId) {
      setDetailSettled(true)
      return
    }
    setLoading(true)
    try {
      const response = await yandexMarketAPI.getProductByOfferId(Number(sellerId), offerId)
      const card = response.data?.data ?? response.data
      if (card && !card.error) setDetail(card)
    } catch (error: any) {
      showToast({
        type: 'error',
        title: 'Не удалось загрузить карточку',
        message: error?.response?.data?.error?.message || error?.message,
        duration: 5000,
      })
    } finally {
      setLoading(false)
      setDetailSettled(true)
    }
  }, [offerId, sellerId, showToast])

  useEffect(() => {
    setDetail(null)
    setDetailSettled(false)
    void loadDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId, offerId])

  // Справочник подписей характеристик категории — best effort: пустой
  // справочник (категория ещё не синхронизирована) не должен ронять карточку,
  // просто parameter_values покажутся по id (спецификация, п.2).
  useEffect(() => {
    setParameterNames({})
    const categoryId = product?.market_category_id
    if (!categoryId) return undefined
    const controller = new AbortController()
    void yandexMarketAPI
      .getCategoryParameters(categoryId, controller.signal)
      .then((response) => {
        const data = response.data?.data ?? response.data
        const list: YmCategoryParameter[] = Array.isArray(data?.parameters) ? data.parameters : []
        setParameterNames(Object.fromEntries(list.map((item) => [Number(item.parameter_id), item])))
      })
      .catch(() => setParameterNames({}))
    return () => controller.abort()
  }, [product?.market_category_id])

  const model = useMemo(
    () => (product && offerId ? buildYmModel(product, offerId, parameterNames, sellerName, sellerId) : null),
    [product, offerId, parameterNames, sellerName, sellerId],
  )

  if (!offerId) return null

  const priceContent = (
    <div className="space-y-4">
      <Panel title="Цена Маркета">
        <div className="grid gap-4 sm:grid-cols-3">
          <ReadonlyValue
            label="Цена кабинета"
            value={product?.basic_price != null ? formatRub(product.basic_price) : null}
            why="площадка не прислала цену"
          />
          <ReadonlyValue label="Валюта" value={product?.currency || null} />
          <ReadonlyValue
            label="Рейтинг контента"
            value={product?.content_rating != null ? `${product.content_rating} %` : null}
            why="Маркет рейтинг не прислал"
          />
        </div>
      </Panel>
      <Panel title="Остатки">
        <Notice>Нет данных: Партнёрский API Маркета остатков в зеркале не даёт.</Notice>
      </Panel>
    </div>
  )

  const overviewExtra = (
    <Panel title="Состояние на Маркете">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <ReadonlyValue label="Market SKU" value={product?.market_sku || null} mono />
        <ReadonlyValue label="Категория" value={product?.market_category_name || null} />
        <ReadonlyValue label="Статус" value={product ? ymStatusLabel(product.card_status) : null} />
        <ReadonlyValue label="Группа склейки" value={product?.group_id || null} mono why="карточка не в группе" />
      </div>
    </Panel>
  )

  return (
    <McardWindow
      platform="ym"
      sellerId={sellerId ? Number(sellerId) : null}
      sellerName={sellerName}
      windowKey={offerId}
      model={model}
      loading={!detail && !detailSettled}
      keys={keys}
      activeKey={product ? rowKeyOf(product) : null}
      onNavigate={onNavigate}
      onClose={onClose}
      canGoBack={canGoBack}
      onBack={onBack}
      onRefresh={() => loadDetail()}
      refreshing={loading}
      refreshLabel="Перечитать данные"
      refreshTitle="Перечитывает сохранённую копию карточки из ПИМ. Саму площадку не опрашивает."
      onReloadDetail={() => void loadDetail()}
      linkParam="offer"
      mediaEditable={false}
      renderMedia={({ openLightbox }) => (
        <Panel title="Фотографии на витрине">
          {!model || model.photos.length === 0 ? (
            <Notice>На витрине нет ни одной фотографии.</Notice>
          ) : (
            <div className="space-y-2">
              <Notice>
                Фото Маркета в окне только читаются, со статусом загрузки. Заменить набор можно кнопкой «Обновить фото из ПИМ…».
              </Notice>
              {model.photos.map((url, index) => (
                <PhotoRow
                  key={`${url}-${index}`}
                  url={url}
                  index={index}
                  uploadState={model.photoStates?.[index] ?? null}
                  onOpen={() => openLightbox(index)}
                />
              ))}
            </div>
          )}
        </Panel>
      )}
      priceContent={priceContent}
      overviewExtra={overviewExtra}
      bundleExternal={{ id: model?.bundleExternalId, label: 'Группа склейки' }}
    />
  )
}

export default YmProductModal
